import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Element } from "@/components/validatedShipments/element";
import { cn } from "@/lib/utils";
import { PickUpLocationFullCompanyDto } from "@/types/api/api-types";

import { SectionTitle } from "./sectionTitle";
import { usePickupDayOptions, usePickupTimeOptions } from "@/hooks/useEnumOptions";

type ZoneInfoProps = {
  pickupLocation: PickUpLocationFullCompanyDto | null;
};

const FALLBACK = "-";

export const ZoneInfo = ({ pickupLocation }: ZoneInfoProps) => {
  const { t } = useTranslation("boLogistics");
  const pickupDayOptions = usePickupDayOptions();
  const pickupTimeOptions = usePickupTimeOptions();
  const zones = pickupLocation?.pickupZones ?? null;

  const activeDays = useMemo(() => new Set(zones?.pickupDays.map((d) => d.pickupDay)), [zones]);

  if (!zones) {
    return <div>{t("pickupDetailsZone.empty")}</div>;
  }

  const findTimeLabel = (value: string) =>
    pickupTimeOptions.find((opt) => opt.value === value)?.label ?? FALLBACK;

  const findDayLong = (value: string) =>
    pickupDayOptions.find((opt) => opt.value === value)?.long ?? FALLBACK;

  return (
    <div className="flex flex-col gap-2">
      <SectionTitle>{t("pickupDetailsZone.title")}</SectionTitle>
      <div className="flex flex-row justify-center gap-2 border-b border-gray-200 py-3">
        <h3 className="flex-1 text-xs font-medium text-gray-600 md:text-sm">{zones.name}</h3>
        <div className="flex-1">
          <div className="h-[20px] w-[20px] rounded-sm" style={{ backgroundColor: zones.color }} />
        </div>
      </div>
      <Element
        title={t("pickupDetailsZone.maxPickup")}
        value={String(zones.maxPickup ?? FALLBACK)}
      />
      <div className="flex flex-col">
        <div className="flex w-full flex-row items-center gap-1">
          {pickupDayOptions.map((day) => (
            <div
              key={day.value}
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 focus:outline-hidden",
                activeDays.has(day.value) ? "bg-brand700 text-white" : "bg-white text-gray-700",
              )}
            >
              {day.label}
            </div>
          ))}
        </div>
        {zones.pickupDays.map((day) => (
          <Element
            key={day.pickupDay}
            title={findDayLong(day.pickupDay)}
            value={findTimeLabel(day.pickupTime)}
            truncate={false}
          />
        ))}
      </div>
    </div>
  );
};
